/**
 * `<CandidateKebabMenu>` — overflow menu on the candidate detail header.
 *
 * Spec: candidate-detail-rebuild-spec.md §2.1.
 *
 * Menu items:
 *   1. "Also consider for another role…" — opens CrossJdModal
 *   2. "Change status…" — opens TenantStatusModal
 *   3. "Re-run AI evaluation" — fires re-eval from current JD context
 *   4. separator
 *   5. "Delete candidate" — destructive, opens DeleteCandidateModal
 *
 * Uses @radix-ui/react-dropdown-menu (already a project dependency) so
 * we get focus management + keyboard nav + ARIA out of the box without
 * hand-writing a dropdown.
 *
 * "Also consider" copies the candidate ID to clipboard (separate kebab item,
 * per spec).
 */
import type { JSX } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { usePortalHost } from "@/components/v2/layout/PortalHostContext";

interface CandidateKebabMenuProps {
  onCrossJd: () => void;
  onChangeStatus: () => void;
  onRerunEval: () => void;
  onCopyId: () => void;
  onDelete: () => void;
}

export function CandidateKebabMenu({
  onCrossJd,
  onChangeStatus,
  onRerunEval,
  onCopyId,
  onDelete,
}: CandidateKebabMenuProps): JSX.Element {
  // Portal into the zoomed shell host so the menu resolves v2 tokens + app scale
  // (see PortalHostContext); `?? undefined` falls back to document.body off-shell.
  const container = usePortalHost() ?? undefined;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="More candidate actions"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)] focus-visible:ring-offset-2"
          data-testid="candidate-kebab-trigger"
        >
          <MoreHorizontal size={16} aria-hidden />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal container={container}>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          // [zoom:var(--app-scale)] scales the menu to match the app-scale dial;
          // the body-zoom-1 host keeps positioning correct. See PortalHostContext.
          className="[zoom:var(--app-scale)] z-50 min-w-[220px] rounded-lg border border-border-default bg-surface-elevated py-1 shadow-lg"
          data-testid="candidate-kebab-menu"
        >
          <DropdownMenu.Item
            onSelect={onCrossJd}
            className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-text-primary outline-none hover:bg-surface-subtle focus:bg-surface-subtle"
            data-testid="kebab-cross-jd"
          >
            Also consider for another role…
          </DropdownMenu.Item>

          <DropdownMenu.Item
            onSelect={onChangeStatus}
            className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-text-primary outline-none hover:bg-surface-subtle focus:bg-surface-subtle"
            data-testid="kebab-change-status"
          >
            Change status…
          </DropdownMenu.Item>

          <DropdownMenu.Item
            onSelect={onRerunEval}
            className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-text-primary outline-none hover:bg-surface-subtle focus:bg-surface-subtle"
            data-testid="kebab-rerun-eval"
          >
            Re-run AI evaluation
          </DropdownMenu.Item>

          <DropdownMenu.Item
            onSelect={onCopyId}
            className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-text-secondary outline-none hover:bg-surface-subtle focus:bg-surface-subtle"
            data-testid="kebab-copy-id"
          >
            Copy candidate ID
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-1 border-t border-border-default" />

          <DropdownMenu.Item
            onSelect={onDelete}
            className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-status-rejected-fg outline-none hover:bg-status-rejected-bg focus:bg-status-rejected-bg"
            data-testid="kebab-delete"
          >
            Delete candidate
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
